import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiTrash2 } from "react-icons/fi";
import styles from "../styles/RevisionWorkspace.module.css";
import { API_URL } from "../config/api";

const RevisionWorkspace = ({ revision, onClose, onRevisionFinished }) => {
  const warehouseId = revision?.warehouseId;
  const token = localStorage.getItem("token");

  const [searchValue, setSearchValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeTab, setActiveTab] = useState("Все");
  const [products, setProducts] = useState([]);

  const calcType = (qty, baseQty) => {
    const q = Number(qty);
    const b = Number(baseQty);
    if (q > b) return "излишек";
    if (q < b) return "недостача";
    return "Не определено";
  };

  useEffect(() => {
    if (revision?.flag) {
      const fetchRevisionItems = async () => {
        try {
          const res = await axios.get(
            `${API_URL}/api/stock_changes/${revision.id}/items`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const items = res.data.data;

          const productsRes = await axios.get(`${API_URL}/api/products`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const allProducts = productsRes.data.data;

          const fetchedProducts = items.map((item) => {
            const productData = allProducts.find((p) => p.id === item.product_id);
            const name = productData ? productData.name_ru : `Товар ${item.product_id}`;
            const qty = Number(item.quantity) || 0;

            return {
              id: item.product_id,
              name,
              barcode: item.ntin || "-",
              qty,
              baseQty: qty,
              price: Number(item.purchase_price) || 0,
              type: item.act === "in" ? "излишек" : "недостача",
            };
          });

          setProducts(fetchedProducts);
        } catch (err) {
          console.error("Ошибка при загрузке элементов ревизии:", err);
        }
      };

      fetchRevisionItems();
    }
  }, [revision, token]);

  const handleFinish = async () => {
    if (products.length === 0) {
      alert("Нет товаров для отправки");
      return;
    }

    const items = products
      .map((p) => {
        const diff = p.qty - p.baseQty;
        if (diff === 0) return null;
        return {
          product_id: p.id,
          type: diff > 0 ? "increase" : "decrease",
          quantity: Math.abs(diff),
        };
      })
      .filter(Boolean);

    if (items.length === 0) {
      alert("Нет изменений для отправки");
      return;
    }

    const payload = {
      type: "revision",
      status: "approved",
      name: revision?.name,
      storage_id: warehouseId,
      items,
    };

    try {
      const res = await axios.post(`${API_URL}/api/stock_changes`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 200 || res.status === 201) {
        if (onRevisionFinished) onRevisionFinished();
      } else {
        alert("Ошибка при отправке ревизии");
      }
    } catch (err) {
      console.error("Ошибка POST запроса:", err);

      if (err.response) {
        console.log("Ответ от сервера:", err.response.data);

        const apiError = err.response.data?.errors;
        if (apiError && apiError.message) {
          alert(`Ошибка: ${apiError.message}`);
        } else if (typeof err.response.data === "string") {
          alert(`Ошибка: ${err.response.data}`);
        } else {
          alert("Произошла ошибка при обработке запроса");
        }
      } else {
        alert("Ошибка соединения с сервером");
      }
    }
  };

  const handleAddProduct = () => {
    const newProduct = {
      id: Date.now(),
      name: "Новый товар",
      barcode: Math.floor(Math.random() * 999999).toString(),
      qty: 0,
      price: 0,
      baseQty: 0,
      type: calcType(0, 0),
    };
    setProducts((prev) => [...prev, newProduct]);
  };

  const handleSelect = (item) => {
    const initialQty = item.quantity || 0;
    const newProduct = {
      id: item.product_id,
      name: item.name_ru,
      barcode: item.ntin || "-",
      qty: initialQty,
      baseQty: initialQty,
      price: item.price || 0,
      type: calcType(initialQty, initialQty),
    };
    setProducts((prev) => [...prev, newProduct]);
    setSearchValue("");
    setShowSuggestions(false);
  };

  const handleDelete = (id) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const handleQtyChange = (id, newQty) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        const qty = Number(newQty);
        const type = calcType(qty, p.baseQty);
        return { ...p, qty, type };
      })
    );
  };

  useEffect(() => {
    if (!warehouseId || !token) return;
    if (searchValue.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        const [byName, byNtin] = await Promise.all([
          axios.get(`${API_URL}/api/stocks`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { storage_id: warehouseId, name_ru: searchValue },
          }),
          axios.get(`${API_URL}/api/stocks`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { storage_id: warehouseId, ntin: searchValue },
          }),
        ]);

        const combined = [...byName.data.data, ...byNtin.data.data];
        const unique = combined.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.product_id === item.product_id)
        );

        setSuggestions(unique);
        setShowSuggestions(true);
        localStorage.setItem("lastProducts", JSON.stringify(unique));
      } catch (err) {
        console.error("Ошибка загрузки товаров:", err);
        const saved = localStorage.getItem("lastProducts");
        if (saved) setSuggestions(JSON.parse(saved));
      }
    };

    const delayDebounce = setTimeout(fetchSuggestions, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchValue, warehouseId, token]);

  const totalSum = products.reduce((sum, p) => sum + p.price * p.qty, 0);
  const shortages = products.filter((p) => p.type === "недостача");
  const surpluses = products.filter((p) => p.type === "излишек");

  // ✅ фильтруем таблицу тем же searchValue (как и UI)
  const term = searchValue.trim().toLowerCase();

  const filteredProducts = products.filter((p) => {
    const matchesSearch =
      !term ||
      p.name.toLowerCase().includes(term) ||
      String(p.barcode || "").includes(term);

    if (activeTab === "Все") return matchesSearch;
    if (activeTab === "Недостача") return matchesSearch && p.type === "недостача";
    if (activeTab === "Излишки") return matchesSearch && p.type === "излишек";
    return false;
  });

  const formattedDate = revision?.created_at
    ? new Date(revision.created_at).toLocaleString("ru-RU")
    : "—";

  const renderContent = () => {
    if (activeTab === "Информация о ревизии") {
      return (
        <div className={styles.revisionInfoWrapper}>
          <table className={styles.revisionInfoTable}>
            <thead>
              <tr>
                <th>Название</th>
                <th>Дата создания</th>
                <th>Статус</th>
                <th>Склад</th>
                <th>Сотрудник</th>
                <th>Номер документа</th>
                <th>Синхронизация</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{revision?.name || "—"}</td>
                <td>{formattedDate}</td>
                <td className={styles.notFinished}>Не завершён</td>
                <td>{revision?.warehouseName || "—"}</td>
                <td>{revision?.employee || "—"}</td>
                <td>{revision?.docNumber || "—"}</td>
                <td>—</td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    if (filteredProducts.length === 0) {
      return (
        <p className={styles.empty}>
          Начните ввод для поиска или добавьте товар
        </p>
      );
    }

    return (
      <table className={styles.productTable}>
        <thead>
          <tr>
            <th>#</th>
            <th>Название</th>
            <th>Штрихкод</th>
            <th>Кол-во</th>
            <th>Цена</th>
            <th>Сумма</th>
            <th>Статус</th>
            {activeTab === "Все" && <th>Удалить</th>}
          </tr>
        </thead>
        <tbody>
          {filteredProducts.map((p, index) => {
            const diff = p.qty - p.baseQty;
            const displayQty = activeTab === "Все" ? p.qty : Math.abs(diff);

            return (
              <tr key={p.id}>
                <td>{index + 1}</td>
                <td>{p.name}</td>
                <td>{p.barcode}</td>
                <td>
                  {activeTab === "Все" ? (
                    <input
                      type="number"
                      className={styles.qtyInput}
                      value={p.qty}
                      min={0}
                      onChange={(e) => handleQtyChange(p.id, e.target.value)}
                    />
                  ) : (
                    <span>{displayQty}</span>
                  )}
                </td>
                <td>{p.price} ₸</td>
                <td>
                  {(
                    p.price * (activeTab === "Все" ? p.qty : Math.abs(diff))
                  ).toLocaleString()}{" "}
                  ₸
                </td>
                <td>{p.type}</td>
                {activeTab === "Все" && (
                  <td>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(p.id)}
                      title="Удалить"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className={styles.workspace}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onClose}>
          <p className={styles.backText}>Редактирование ревизии</p>
        </button>
      </div>

      <div className={styles.topBar}>
        <button className={styles.btnPrimary}>Скачать ревизию</button>
        <button className={styles.btnPrimary}>Обновить остаток</button>
      </div>

      <div className={styles.cards}>
        <div className={styles.card}>
          <h4>Недостача</h4>
          <p className={styles.red}>
            {shortages
              .reduce((sum, p) => sum + Math.abs(p.qty - p.baseQty) * p.price, 0)
              .toLocaleString()}{" "}
            ₸
          </p>
          <span>
            Кол-во:{" "}
            {shortages.reduce((sum, p) => sum + Math.abs(p.qty - p.baseQty), 0)}
          </span>
        </div>

        <div className={styles.card}>
          <h4>Излишки</h4>
          <p className={styles.orange}>
            {surpluses
              .reduce((sum, p) => sum + Math.abs(p.qty - p.baseQty) * p.price, 0)
              .toLocaleString()}{" "}
            ₸
          </p>
          <span>
            Кол-во:{" "}
            {surpluses.reduce((sum, p) => sum + Math.abs(p.qty - p.baseQty), 0)}
          </span>
        </div>

        <div className={styles.card}>
          <h4>Итоговая разница</h4>
          <p className={styles.green}>
            {(
              surpluses.reduce(
                (s, p) => s + Math.abs(p.qty - p.baseQty) * p.price,
                0
              ) -
              shortages.reduce(
                (s, p) => s + Math.abs(p.qty - p.baseQty) * p.price,
                0
              )
            ).toLocaleString()}{" "}
            ₸
          </p>
        </div>

        <div className={styles.card}>
          <h4>Общая сумма товаров</h4>
          <p className={styles.blue}>{totalSum.toLocaleString()} ₸</p>
        </div>
      </div>

      <div className={styles.tabs}>
        {["Все", "Недостача", "Излишки", "Информация о ревизии"].map((tab) => (
          <button
            key={tab}
            className={`${styles.tab} ${
              activeTab === tab ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab !== "Информация о ревизии" && (
        <div className={styles.searchBar}>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Поиск товара по названию или штрихкоду"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
          />

          {showSuggestions && suggestions.length > 0 && (
            <ul className={styles.suggestionList}>
              {suggestions.map((item) => (
                <li
                  key={item.product_id}
                  className={styles.suggestionItem}
                  onClick={() => handleSelect(item)}
                >
                  {item.name_ru} — {item.quantity} шт.
                </li>
              ))}
            </ul>
          )}

          <button className={styles.btnAdd} onClick={handleAddProduct}>
            Добавить товар
          </button>
          <button className={styles.btnBlack} onClick={handleFinish}>
            Завершить
          </button>
        </div>
      )}

      <div className={styles.table}>{renderContent()}</div>
    </div>
  );
};

export default RevisionWorkspace;
